import React from 'react';
import { motion } from 'framer-motion';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { Input } from '../components/ui/input';
import { MapPin, Users, Music, Wine, Crown, Sparkles, ArrowRight, Clock, DollarSign } from 'lucide-react';

export function FeaturedEventSection({ navigate }) {
  return (
    <motion.section
      id="events"
      data-testid="featured-event-section"
      className="py-16 md:py-24 bg-[var(--bg-2)]"
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.55 }}
    >
      <div className="max-w-[1120px] mx-auto px-4 sm:px-6">
        <Card className="overflow-hidden border border-[var(--border-strong)] bg-[var(--surface)] rounded-[var(--radius-lg)]">
          <div className="relative aspect-[4/5] sm:aspect-[3/4]">
            <img src="https://images.unsplash.com/photo-1470225620780-dba8ba36b745?w=900&q=80" alt="Event" className="w-full h-full object-cover" loading="lazy" />
            <div className="absolute inset-0 bg-gradient-to-t from-black via-black/60 to-transparent"></div>
            <Badge className="absolute top-4 left-4 bg-[var(--accent-gold)] text-black font-semibold">FEATURED</Badge>
            <div className="absolute bottom-0 left-0 right-0 p-6 sm:p-8">
              <div className="font-mono text-xs tracking-[0.18em] uppercase text-[var(--text-muted)] mb-3">This Saturday · 10PM–4AM</div>
              <h2 className="font-bebas text-4xl sm:text-5xl leading-tight mb-3">MIDNIGHT<br /><span className="text-[var(--accent-teal)]">NOIR</span></h2>
              <p className="text-sm text-[var(--text-dim)] mb-4">Deep house & techno. No photos.</p>
              <Button data-testid="featured-event-reserve-button" className="btn-gold-solid rounded-xl" onClick={() => navigate('/checkin')}>
                Reserve Spot<ArrowRight size={16} className="ml-2" />
              </Button>
            </div>
          </div>
        </Card>
      </div>
    </motion.section>
  );
}

export function UpcomingEventsGrid({ navigate }) {
  const events = [
    { id: 1, date: { day: '08', month: 'MAR' }, title: 'ELECTRIC FRIDAYS', genre: 'EDM',    cover: 2000, image: '/images/home/scratch-night-03.jpg' },
    { id: 2, date: { day: '09', month: 'MAR' }, title: 'SOUL SESSIONS',   genre: 'R&B',    cover: 1800, image: '/images/home/scratch-night-06.jpg' },
    { id: 3, date: { day: '10', month: 'MAR' }, title: 'AFTER DARK',      genre: 'Techno', cover: 2200, image: '/images/home/scratch-night-09.jpg' },
  ];

  return (
    <motion.section data-testid="upcoming-events-section" className="py-16 md:py-24" initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }}>
      <div className="max-w-[1120px] mx-auto px-4 sm:px-6">
        <h2 className="font-bebas text-4xl sm:text-5xl mb-10">UPCOMING NIGHTS</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {events.map((event) => (
            <Card key={event.id} data-testid={`event-card-${event.id}`} className="group relative overflow-hidden rounded-[var(--radius-lg)] border border-[var(--border-strong)] bg-[var(--surface)] hover:shadow-[var(--shadow-card)] transition-all">
              <div className="relative aspect-[3/4]">
                <img src={event.image} alt={event.title} className="w-full h-full object-cover opacity-40" loading="lazy" />
                <div className="absolute inset-0 bg-gradient-to-b from-transparent via-black/50 to-black"></div>
                <div className="absolute inset-0 p-6 flex flex-col justify-between">
                  <div className="self-start font-mono text-center">
                    <div className="text-4xl font-bold">{event.date.day}</div>
                    <div className="text-xs tracking-[0.18em] text-[var(--text-muted)]">{event.date.month}</div>
                  </div>
                  <div>
                    <span className="text-xs px-2 py-1 rounded-full bg-white/10 border border-white/20 text-[var(--text-dim)] mb-3 inline-block">{event.genre}</span>
                    <h3 className="font-bebas text-3xl leading-tight mb-2">{event.title}</h3>
                    <p className="text-sm text-[var(--text-dim)] mb-4">₹{event.cover.toLocaleString()} Cover</p>
                    <Button size="sm" data-testid={`event-card-buy-tickets-button-${event.id}`} className="btn-gold-solid rounded-xl w-full text-xs font-semibold" onClick={() => navigate('/checkin')}>Get Tickets</Button>
                  </div>
                </div>
              </div>
            </Card>
          ))}
        </div>
      </div>
    </motion.section>
  );
}

export function ExperienceSection() {
  return (
    <motion.section data-testid="experience-section" className="py-16 md:py-24 bg-[var(--bg-2)]" initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }}>
      <div className="max-w-[1120px] mx-auto px-4 sm:px-6">
        <h2 className="font-bebas text-4xl sm:text-5xl mb-6">THE SCRATCH<br /><span className="text-[var(--accent-lime)]">EXPERIENCE</span></h2>
        <Accordion type="single" collapsible className="space-y-3">
          <AccordionItem value="sound" className="border border-[var(--border-custom)] rounded-xl px-4 bg-[var(--surface)]">
            <AccordionTrigger className="hover:no-underline">
              <div className="flex items-center gap-3"><Music size={20} className="text-[var(--accent-gold)]" /><span className="font-semibold">World-Class Sound</span></div>
            </AccordionTrigger>
            <AccordionContent className="text-sm text-[var(--text-dim)] pb-4">Funktion-One sound system engineered for the perfect sonic experience.</AccordionContent>
          </AccordionItem>
          <AccordionItem value="bar" className="border border-[var(--border-custom)] rounded-xl px-4 bg-[var(--surface)]">
            <AccordionTrigger className="hover:no-underline">
              <div className="flex items-center gap-3"><Wine size={20} className="text-[var(--accent-gold)]" /><span className="font-semibold">Premium Bar</span></div>
            </AccordionTrigger>
            <AccordionContent className="text-sm text-[var(--text-dim)] pb-4">Craft cocktails, rare spirits, and exclusive bottle service.</AccordionContent>
          </AccordionItem>
          <AccordionItem value="vip" className="border border-[var(--border-custom)] rounded-xl px-4 bg-[var(--surface)]">
            <AccordionTrigger className="hover:no-underline">
              <div className="flex items-center gap-3"><Crown size={20} className="text-[var(--accent-gold)]" /><span className="font-semibold">VIP Treatment</span></div>
            </AccordionTrigger>
            <AccordionContent className="text-sm text-[var(--text-dim)] pb-4">Private tables, dedicated hosts, reserved for the discerning few.</AccordionContent>
          </AccordionItem>
        </Accordion>
      </div>
    </motion.section>
  );
}

export function VIPSection({ navigate }) {
  return (
    <motion.section id="vip" data-testid="vip-section" className="py-16 md:py-24" initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }}>
      <div className="max-w-[1120px] mx-auto px-4 sm:px-6">
        <Card className="relative overflow-hidden border-2 border-[var(--accent-gold)] bg-[var(--surface)] rounded-[var(--radius-lg)] p-8 md:p-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <div>
              <Badge className="mb-4 bg-[var(--accent-gold)] text-black font-semibold">EXCLUSIVE</Badge>
              <h2 className="font-bebas text-4xl sm:text-5xl leading-tight mb-4">ELEVATE YOUR<br /><span className="text-[var(--accent-gold)]">EXPERIENCE</span></h2>
              <p className="text-sm text-[var(--text-dim)] mb-6">VIP tables, priority entry, dedicated service.</p>
              <div className="space-y-2 mb-8">
                {['Priority entry', 'Premium bottle service', 'Dedicated table host', 'VIP lounge access'].map((perk) => (
                  <div key={perk} className="flex items-center gap-3 text-sm">
                    <Sparkles size={14} className="text-[var(--accent-gold)]" />
                    <span className="text-[var(--text-dim)]">{perk}</span>
                  </div>
                ))}
              </div>
              <Button data-testid="vip-inquiry-open-dialog-button" className="btn-gold-solid rounded-xl" onClick={() => navigate('/checkin')}>
                Inquire Now<ArrowRight size={16} className="ml-2" />
              </Button>
            </div>
            <div className="space-y-3">
              {[
                { name: 'Standard Table', price: '₹15,000', capacity: '4-6 guests' },
                { name: 'Premium Table', price: '₹25,000', capacity: '6-8 guests' },
                { name: 'Elite Booth', price: '₹40,000', capacity: '8-10 guests' }
              ].map((tier) => (
                <Card key={tier.name} className="p-5 bg-black/40 border border-[var(--border-custom)] rounded-xl">
                  <div className="flex justify-between items-start mb-2">
                    <h4 className="font-semibold">{tier.name}</h4>
                    <span className="text-[var(--accent-gold)] font-semibold">{tier.price}</span>
                  </div>
                  <p className="text-xs text-[var(--text-muted)]">{tier.capacity}</p>
                </Card>
              ))}
            </div>
          </div>
        </Card>
      </div>
    </motion.section>
  );
}

export function GallerySection() {
  const images = [
    { src: '/images/gallery-1.jpg', alt: 'Ornate entrance with golden archways' },
    { src: '/images/gallery-2.webp', alt: 'Vibrant party atmosphere with neon lights' },
    { src: '/images/gallery-3.webp', alt: 'Premium bottle service experience' },
    { src: '/images/gallery-4.webp', alt: 'Happy guests at Scratch Unleashed' },
    { src: '/images/gallery-5.webp', alt: 'Dance floor with geometric lighting' },
    { src: '/images/gallery-1.jpg', alt: 'VIP entrance detail' }
  ];

  return (
    <motion.section id="gallery" data-testid="gallery-section" className="py-16 md:py-24 bg-[var(--bg-2)]" initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }}>
      <div className="max-w-[1120px] mx-auto px-4 sm:px-6">
        <h2 className="font-bebas text-4xl sm:text-5xl mb-8">CAPTURED<br /><span className="text-[var(--accent-teal)]">MOMENTS</span></h2>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          {images.map((img, index) => (
            <div key={index} data-testid={`gallery-tile-${index}`} className="relative aspect-square overflow-hidden rounded-xl border border-[var(--border-custom)] hover:border-[var(--border-strong)] transition-all group cursor-pointer">
              <img src={img.src} alt={img.alt} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" loading="lazy" />
            </div>
          ))}
        </div>
      </div>
    </motion.section>
  );
}

export function FinalCTASection({ navigate }) {
  return (
    <motion.section data-testid="final-cta-section" className="relative py-20 md:py-32 overflow-hidden" initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }}>
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-0">
        <h2 className="outlined-giant-word font-bebas text-[100px] sm:text-[160px] lg:text-[220px] whitespace-nowrap">UNLEASHED</h2>
      </div>
      <div className="relative z-10 max-w-[1120px] mx-auto px-4 sm:px-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
          <div>
            <h2 className="font-bebas text-4xl sm:text-5xl leading-tight mb-4">YOUR NIGHT<br /><span className="text-[var(--accent-gold)]">AWAITS</span></h2>
            <p className="text-sm md:text-base text-[var(--text-dim)] mb-6">Join the guest list or reserve your table.<br />Limited capacity. Instant confirmation.</p>
          </div>
          <div className="space-y-3">
            <div className="flex gap-3">
              <Input data-testid="final-cta-email-input" type="email" placeholder="Your email" className="bg-[var(--surface)] border-[var(--border-custom)] rounded-xl" />
              <Button data-testid="final-cta-submit-button" className="btn-gold-solid rounded-xl px-6">Submit</Button>
            </div>
            <p className="text-xs text-[var(--text-muted)]">✓ Instant confirmation · ✓ Limited spots</p>
          </div>
        </div>
      </div>
    </motion.section>
  );
}
